import { io, type Socket } from 'socket.io-client'

/**
 * Realtime event names on the default Socket.IO namespace. These MUST match the
 * server emit sites (server/pkg/socketio/post_events.go, post_controller.go) and
 * the mobile client (mobile/lib/core/domain/enums/io_event_type.enum.dart) — the
 * server fans the SAME payloads to web and mobile.
 *
 *  - `new_post`      → a post landed in a hive board room (room = hive id).
 *  - `new_comment`   → a comment/reply landed in a post room (room = post id) —
 *                      also fanned to the board room.
 *  - `post_updated`  → a post's fields changed (edit, media READY flip, like) —
 *                      fanned to the post's room(s). Payload is the full post.
 *  - `post_deleted`  → a post was removed — payload is the bare post id string.
 */
export const RealtimeEvent = {
  NewPost: 'new_post',
  NewComment: 'new_comment',
  PostUpdated: 'post_updated',
  PostDeleted: 'post_deleted',
} as const

export type RealtimeEventName = (typeof RealtimeEvent)[keyof typeof RealtimeEvent]

/** Client→server control events (join/leave a room, announce identity). */
const CONTROL = {
  Connect: 'io_connect',
  JoinRoom: 'join_room',
  LeaveRoom: 'leave_room',
} as const

/**
 * Creates the app's single Socket.IO connection to the Go realtime server.
 *
 * Tokenless handshake — the default namespace runs in GRACE mode
 * (server/pkg/socketio/default_auth.go), so a socket announces its identity by
 * emitting `io_connect` with the User id rather than presenting a JWT (the web
 * auth token is an http-only cookie and isn't readable from JS anyway).
 *
 * Transports: WebSocket only, like the mobile client. The prod API runs several
 * tasks behind a load balancer without sticky sessions. A polling session opens
 * on one task, its follow-up requests reach another that never issued the id
 * (`400 Session ID unknown`), and the socket never connects. One WebSocket stays
 * on the task that opened it. A WebSocket upgrade only fails (`400 Bad request`)
 * for a page Origin missing from the server's allowlist, `socketAllowedOrigins()`
 * in server/pkg/socketio/server.go.
 *
 * `autoConnect: false` so the caller can wire listeners before the first
 * connect; call `.connect()` once wired.
 */
export function createSocket(url: string): Socket {
  return io(url, {
    transports: ['websocket'],
    autoConnect: false,
    reconnection: true,
    reconnectionDelay: 3000,
  })
}

/** Announce identity on (re)connect — the grace-mode handshake. No-op if userId is empty. */
export function announceIdentity(socket: Socket, userId: string | undefined): void {
  if (!userId) return
  socket.emit(CONTROL.Connect, userId)
}

/** Join a room by bare id (hive id for a board feed, post id for a thread). */
export function joinRoom(socket: Socket, roomId: string): void {
  if (!roomId) return
  socket.emit(CONTROL.JoinRoom, roomId)
}

/** Leave a room by bare id. */
export function leaveRoom(socket: Socket, roomId: string): void {
  if (!roomId) return
  socket.emit(CONTROL.LeaveRoom, roomId)
}
